import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Badge, Tag, Typography, Popconfirm } from 'antd';
import { MinusOutlined, PlusOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';

const { Text } = Typography;

const CartItemRow = React.memo(({ index, style, cart, removedItemId, updateCartItemQuantity, handleRemoveItem, getDiscountedPrice, formatImageUrl }) => {
  const item = cart[index];
  const isRemoving = removedItemId === item.id;
  const itemPrice = item.price;
  const discountedPrice = getDiscountedPrice(item);
  const hasDiscount = discountedPrice < itemPrice;
  const totalItemPrice = discountedPrice * item.quantity;

  return (
    <motion.div
      style={style}
      key={item.id}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: isRemoving ? 0 : 1, scale: isRemoving ? 0.5 : 1.05, height: isRemoving ? 0 : 'auto' }}
      exit={{ opacity: 0, scale: 0.5, height: 0 }}
      transition={{ type: "spring", stiffness: 600, damping: 35, opacity: { duration: 0.25 } }}
      className="cart-item-card glass-effect" layout
    >
      {/* Badge flotante de producto en el carrito */}
      <div className="product-in-cart-badge">
        <span className="product-in-cart-check">✔</span> En tu carrito
      </div>
      <div className="cart-item-content">
        <div className="cart-item-image-container">
          <Link to={`/product/${item.id}`}>
            <div className="image-wrapper">
              <img
                src={formatImageUrl(item.image)}
                alt={item.name}
                className="cart-item-image"
                onError={(e) => { e.target.onerror = null; e.target.src = '/assets/placeholder.png'; }}
              />
              {hasDiscount && (
                <div className="discount-badge">
                  <span className="discount-value">-{item.promotions[0].value}%</span>
                </div>
              )}
            </div>
          </Link>
        </div>
        <div className="cart-item-details">
          <Link to={`/product/${item.id}`} className="cart-item-name">{item.name}</Link>
          <div className="cart-item-info">
            <Tag className="cart-item-category">{item.categoryName || 'Sin categoría'}</Tag>
            {item.stock <= 5 && item.stock > 0 && (
              <Badge count={`¡Solo ${item.stock}!`} className="stock-badge" style={{ backgroundColor: '#FF9500' }} />
            )}
          </div>
          <div className="cart-item-price-container">
            {hasDiscount ? (
              <div className="price-with-discount">
                <Text delete className="original-price">Bs. {item.price.toFixed(2)}</Text>
                <Text className="discounted-price">Bs. {discountedPrice.toFixed(2)}</Text>
              </div>
            ) : (
              <Text className="cart-item-price">Bs. {item.price.toFixed(2)}</Text>
            )}
          </div>
        </div>
        <div className="cart-item-actions">
          <div className="quantity-control">
            <Button type="text" icon={<MinusOutlined />} className="quantity-button minus"
              onClick={() => updateCartItemQuantity(item.id, item.quantity - 1)} disabled={item.quantity <= 1} />
            <motion.div className="quantity-display" key={item.quantity}
              animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.3 }}>
              {item.quantity}
            </motion.div>
            <Button type="text" icon={<PlusOutlined />} className="quantity-button plus"
              onClick={() => updateCartItemQuantity(item.id, item.quantity + 1)} disabled={item.quantity >= (item.stock || 99)} />
          </div>
          <Popconfirm
            title="¿Eliminar este producto?"
            description="Se eliminará este producto de tu carrito"
            onConfirm={() => handleRemoveItem(item.id)}
            okText="Sí" cancelText="No" placement="left"
            icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
          >
            <Button type="text" danger icon={<DeleteOutlined />} className="remove-button" aria-label="Eliminar producto" />
          </Popconfirm>
        </div>
      </div>
      <div className="cart-item-total">
        <div className="cart-item-total-content">
          <span className="total-label">Subtotal:</span>
          <motion.span className="total-value" key={totalItemPrice}
            animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 0.3 }}>
            Bs. {totalItemPrice.toFixed(2)}
          </motion.span>
        </div>
      </div>
    </motion.div>
  );
});

export default CartItemRow;